/* iter55i — Microsoft Clarity tracker injector.
 *
 * Fetches `/api/clarity/config` on mount. If the admin has saved a
 * `project_id`, the standard Clarity snippet is injected into <head>.
 * We de-dupe by tagging the script with `data-clarity-injected` so
 * hot-reloads and route changes don't double-inject.
 *
 * The tracker is intentionally *global* — it lives at the top of the
 * App tree (see App.js) so all pages (landing, bookstore, editor,
 * admin) get heat-map coverage. Nothing renders visually.
 *
 * If `project_id` is empty (unconfigured or admin cleared it), we
 * quietly no-op so the app works fine before Clarity is set up.
 */
import { useEffect } from 'react';
import { clarityApi } from '@/lib/api';

const SCRIPT_MARKER = 'data-clarity-injected';

function injectClarity(projectId) {
  if (typeof document === 'undefined') return;
  // Already injected? (either by us or a hard-coded snippet.)
  if (document.querySelector(`script[${SCRIPT_MARKER}]`)) return;
  if (window.clarity) return;

  const s = document.createElement('script');
  s.type = 'text/javascript';
  s.async = true;
  s.setAttribute(SCRIPT_MARKER, projectId);
  // Official Clarity boot snippet (from clarity.microsoft.com/setup).
  s.text = `
    (function(c,l,a,r,i,t,y){
      c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
      t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
      y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "${projectId}");
  `;
  document.head.appendChild(s);
}

export default function ClarityTracker() {
  useEffect(() => {
    let cancelled = false;
    clarityApi.publicConfig().then((cfg) => {
      if (cancelled) return;
      if (cfg?.project_id && cfg?.enabled) {
        injectClarity(cfg.project_id);
      }
    }).catch(() => { /* silent — analytics never blocks the app */ });
    return () => { cancelled = true; };
  }, []);
  return null;
}
